import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import { Container, Row, Col, Form, Alert, FormCheck } from 'react-bootstrap';
import MicrosoftSSO from './MicrosoftSSO';
import GoogleSSO from './GoogleSSO';
import Footer from './Footer';
import { FaRocket, FaShieldAlt, FaFileAlt, FaWrench, FaEye, FaEyeSlash } from 'react-icons/fa';
import './Login.css';
import { resetSessionReduxState } from '../redux/sessionReset';

const Login = ({ setIsAuthenticated }) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const savedCredentials = localStorage.getItem('savedCredentials');
    if (savedCredentials) {
      try {
        const { identifier: savedIdentifier, password: savedPassword, rememberMe: savedRememberMe } = JSON.parse(savedCredentials);
        setIdentifier(savedIdentifier || '');
        setPassword(savedPassword || '');
        setRememberMe(savedRememberMe || false);
      } catch (error) {
        console.error('Error parsing saved credentials:', error);
        localStorage.removeItem('savedCredentials');
      }
    }
  }, []);

  const handleRememberMeChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    if (checked) {
      const credentialsToSave = { identifier, password, rememberMe: true };
      localStorage.setItem('savedCredentials', JSON.stringify(credentialsToSave));
    } else {
      localStorage.removeItem('savedCredentials');
    }
  };

  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    setIdentifier(value);
    if (rememberMe) {
      const savedCredentials = localStorage.getItem('savedCredentials');
      if (savedCredentials) {
        try {
          const credentials = JSON.parse(savedCredentials);
          credentials.identifier = value;
          localStorage.setItem('savedCredentials', JSON.stringify(credentials));
        } catch (error) {
          console.error('Error updating saved credentials:', error);
        }
      }
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (rememberMe) {
      const savedCredentials = localStorage.getItem('savedCredentials');
      if (savedCredentials) {
        try {
          const credentials = JSON.parse(savedCredentials);
          credentials.password = value;
          localStorage.setItem('savedCredentials', JSON.stringify(credentials));
        } catch (error) {
          console.error('Error updating saved credentials:', error);
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, { identifier, password });
      const { token } = response.data;
      if (rememberMe) {
        localStorage.setItem('token', token);
      } else {
        localStorage.setItem('token', token);
      }
      if (rememberMe) {
        const credentialsToSave = { identifier, password, rememberMe: true };
        localStorage.setItem('savedCredentials', JSON.stringify(credentialsToSave));
      } else {
        localStorage.removeItem('savedCredentials');
      }
      resetSessionReduxState();
      setIsAuthenticated(true);
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    }
  };

  const handleMicrosoftLoginSuccess = async (userInfo) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/microsoft-sso`, {
        email: userInfo.email,
        name: userInfo.name,
        microsoftId: userInfo.id,
        accessToken: userInfo.accessToken,
        idToken: userInfo.idToken,
      });

      const { token } = response.data;

      if (rememberMe) {
        localStorage.setItem('token', token);
        localStorage.setItem('microsoftToken', userInfo.accessToken);
        localStorage.setItem('microsoftUserInfo', JSON.stringify(userInfo));
      } else {
        localStorage.setItem('token', token);
        sessionStorage.setItem('microsoftToken', userInfo.accessToken);
        sessionStorage.setItem('microsoftUserInfo', JSON.stringify(userInfo));
      }

      resetSessionReduxState();
      setIsAuthenticated(true);
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.message || 'Microsoft SSO login failed. Please try again.');
    }
  };

  const handleMicrosoftLoginError = (error) => {
    setError('Microsoft SSO login failed. Please try again.');
    console.error('Microsoft SSO error:', error);
  };

  const handleGoogleLoginSuccess = async (userInfo) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/google-sso`, {
        email: userInfo.email,
        name: userInfo.name,
        googleId: userInfo.id,
        accessToken: userInfo.accessToken,
        idToken: userInfo.idToken,
        picture: userInfo.picture,
      });

      const { token } = response.data;

      if (rememberMe) {
        localStorage.setItem('token', token);
        localStorage.setItem('googleToken', userInfo.accessToken);
        localStorage.setItem('googleUserInfo', JSON.stringify(userInfo));
      } else {
        localStorage.setItem('token', token);
        sessionStorage.setItem('googleToken', userInfo.accessToken);
        sessionStorage.setItem('googleUserInfo', JSON.stringify(userInfo));
      }

      resetSessionReduxState();
      setIsAuthenticated(true);
      navigate('/home');
    } catch (err) {
      setError(err.response?.data?.message || 'Google SSO login failed. Please try again.');
    }
  };

  const handleGoogleLoginError = (error) => {
    setError('Google SSO login failed. Please try again.');
    console.error('Google SSO error:', error);
  };

  const features = [
    {
      icon: FaRocket,
      title: 'AI-Powered Solutions',
      description: 'Leverage cutting-edge AI technology for automated test case generation, BRD to FRD conversion, and intelligent bug reporting.'
    },
    {
      icon: FaShieldAlt,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with role-based access control and secure data handling for your sensitive projects.'
    },
    {
      icon: FaFileAlt,
      title: 'Faster Development',
      description: 'Reduce development time by up to 70% with automated test generation and intelligent documentation conversion.'
    },
    {
      icon: FaWrench,
      title: 'Comprehensive Tools',
      description: 'Complete QA automation suite including Selenium scripts, API testing, and test data generation.'
    }
  ];

  const bgImageUrl = `${process.env.PUBLIC_URL || ''}/images/mainbg.png`;

  return (
    <div className="login-page" style={{ backgroundImage: `url(${bgImageUrl})` }}>
      <div className="login-background" style={{ backgroundImage: `url(${bgImageUrl})` }}></div>
      <Container fluid className="login-container">
        <Row className="g-0 login-row">
          {/* Left Panel: Features */}
          <Col lg={5} className="features-panel order-2 order-lg-0">
            <div className="features-content">
              {/* Logo Section */}
              <div className="features-logo">
                <div className="logo-container">
                  <img 
                    src="/logonew.png" 
                    alt="Continuum Logo" 
                    className="logo-image"
                  />
                
                </div>
              </div>
              
              <div className="features-header">
                <h1 className="features-title">Why Choose Continuum?</h1>
                <div className="features-divider"></div>
              </div>
              
              <div className="features-list">
                {features.map((feature, index) => {
                  const IconComponent = feature.icon;
                  return (
                    <div key={index} className="feature-item">
                      <div className="feature-icon">
                        <IconComponent style={{ color: '#ffffff' }} />
                      </div>
                      <div className="feature-content">
                        <h3 className="feature-title">{feature.title}</h3>
                        <p className="feature-description">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Col>

          {/* Right Panel: Sign In Form */}
          <Col lg={7} className="signin-panel order-1 order-lg-0">
            <div className="signin-content">
              <h2 className="signin-title">Sign In</h2>
              
              <Form onSubmit={handleSubmit} className="signin-form">
                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label className="form-label">
                    Email or Username
                  </Form.Label>
                  <Form.Control
                    type="text"
                    id="identifier"
                    value={identifier}
                    onChange={handleIdentifierChange}
                    placeholder="your@email.com or username"
                    required
                    autoFocus
                    className="form-input"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="form-label">
                    Password
                  </Form.Label>
                  <div className="password-input-wrap">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      value={password}
                      onChange={handlePasswordChange}
                      required
                      className="form-input password-input"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <FaEyeSlash size={18} aria-hidden />
                      ) : (
                        <FaEye size={18} aria-hidden />
                      )}
                    </button>
                  </div>
                </Form.Group>

                <div className="d-flex align-items-center justify-content-between mb-4">
                  <FormCheck
                    type="checkbox"
                    id="rememberMe"
                    label="Remember Me"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    className="remember-checkbox"
                  />
                  <Link 
                    to="/forgot-password" 
                    className="forgot-password-link"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <button 
                  type="submit" 
                  className="signin-button"
                >
                  SIGN IN
                  <span className="button-arrow">→</span>
                </button>
              </Form>

              <div className="or-divider">
                <div className="or-line"></div>
                <span className="or-text">OR</span>
                <div className="or-line"></div>
              </div>

              <div className="sso-buttons">
                <GoogleSSO
                  onLoginSuccess={handleGoogleLoginSuccess}
                  onLoginError={handleGoogleLoginError}
                />
                <MicrosoftSSO
                  onLoginSuccess={handleMicrosoftLoginSuccess}
                  onLoginError={handleMicrosoftLoginError}
                />
              </div>

              <div className="create-account-section">
                <p className="create-account-text">
                  Don't have an account? <Link to="/register" className="create-account-link">Create Account</Link>
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <Footer />
    </div>
  );
};

export default Login;
